import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ArrowLeft, ArrowRight, Check, LogOut, Send } from "lucide-react";
import Nav from "@/components/site/Nav";
import { Input } from "@/components/ui/input";
import { API_BASE } from "@/lib/api";

const STEPS = ["About You", "Your Goal", "Lifestyle", "Food", "Activity", "Current Nutrition", "Health", "Consent"];
const GOALS = ["Fat loss", "Muscle", "Performance", "Maintenance", "General wellness"];
const DIET_TYPES = ["Vegetarian", "Eggetarian", "Non-vegetarian", "Vegan", "Jain"];
const GS_CUISINES = [
  ["indian", "Indian"], ["american", "American"], ["african", "African"],
  ["middle_eastern", "Middle Eastern"], ["mediterranean", "Mediterranean"],
  ["latin", "Latin"], ["japanese", "Japanese"],
];
const INDIAN_REGIONS = [
  ["north", "North Indian"], ["south", "South Indian"], ["east", "East Indian"],
  ["west", "West Indian"], ["north_east", "North-East Indian"],
];
const inputCls = "bg-white border-[#E5E2D9] h-11 rounded-xl";
const areaCls = "w-full bg-white border border-[#E5E2D9] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#89937C]/30 resize-none";

export default function DietOnboarding() {
  const [member, setMember] = useState(undefined);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [draftState, setDraftState] = useState("idle");
  const restoredRef = useRef(false);
  const saveTimer = useRef(null);
  const [f, setF] = useState({
    name: "", age: "", sex: "", country: "India", timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "", dob: "",
    height_cm: "", weight_kg: "", waist_cm: "",
    goals: [], goal_note: "",
    cuisine_mode: "", cuisine_single: "", cuisine_days: ["", "", "", "", "", "", ""],
    cuisine_single_region: "", cuisine_day_regions: ["", "", "", "", "", "", ""],
    occupation: "", sleep: "", stress: "", schedule: "", travel: "", meal_timing: "",
    diet_type: "", cuisine: "", dislikes: "", allergies: "", cooking: "", typical_meals: "", snacks: "", beverages: "",
    activities: "", frequency: "",
    typical_day: "",
    conditions: "", medications: "", supplements: "", labs: "", existing_guidance: "",
    consent: false,
  });
  const navigate = useNavigate();
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const logout = async () => {
    try { await axios.post(`${API_BASE}/member/auth/logout`, {}, { withCredentials: true }); } catch { /* already out */ }
    toast.success("Logged out — see you soon!");
    navigate("/");
  };

  useEffect(() => {
    axios.get(`${API_BASE}/member/auth/me`, { withCredentials: true })
      .then(async (r) => {
        setMember(r.data);
        if (r.data?.name) setF((v) => ({ ...v, name: v.name || r.data.name }));
        try {
          const d = await axios.get(`${API_BASE}/diet/onboarding/draft`, { withCredentials: true });
          if (d.data?.draft?.data) {
            setF((v) => ({ ...v, ...d.data.draft.data }));
            setStep(d.data.draft.step || 0);
            toast.success("Welcome back — we saved your progress. Continue where you left off.");
          }
        } catch { /* no draft */ }
        restoredRef.current = true;
      })
      .catch(() => setMember(null));
  }, []);

  useEffect(() => {
    if (!member || !restoredRef.current || submitting) return;
    setDraftState("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      axios.put(`${API_BASE}/diet/onboarding/draft`, { data: f, step }, { withCredentials: true })
        .then(() => setDraftState("saved"))
        .catch(() => setDraftState("idle"));
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [f, step]); // eslint-disable-line

  const validate = () => {
    if (step === 0 && (!f.name.trim() || !f.age || !f.height_cm || !f.weight_kg)) return "Please fill name, age, height and weight";
    if (step === 1 && f.goals.length === 0) return "Please select at least one goal (you can pick more than one)";
    if (step === 7 && !f.consent) return "Please read and accept the acknowledgement";
    return null;
  };

  const next = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (step < 7) { setStep(step + 1); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/diet/onboarding`, {
        about: { name: f.name, age: f.age, dob: f.dob || null, sex: f.sex, country: f.country, timezone: f.timezone, height_cm: f.height_cm, weight_kg: f.weight_kg, waist_cm: f.waist_cm || null },
        goal: { primary: f.goals.join(", "), note: f.goal_note },
        lifestyle: { occupation: f.occupation, sleep: f.sleep, stress: f.stress, schedule: f.schedule, travel: f.travel, meal_timing: f.meal_timing },
        food: { diet_type: f.diet_type, cuisine: f.cuisine, dislikes: f.dislikes, allergies: f.allergies, cooking: f.cooking, typical_meals: f.typical_meals, snacks: f.snacks, beverages: f.beverages, global_cuisine: f.cuisine_mode ? { mode: f.cuisine_mode, single: f.cuisine_single || null, single_region: (f.cuisine_single === "indian" && f.cuisine_single_region) || null, days: f.cuisine_mode === "mix" ? f.cuisine_days : null, day_regions: f.cuisine_mode === "mix" ? f.cuisine_day_regions : null } : null },
        activity: { activities: f.activities, frequency: f.frequency },
        current_nutrition: { typical_day: f.typical_day },
        health: { conditions: f.conditions, medications: f.medications, supplements: f.supplements, labs: f.labs, existing_guidance: f.existing_guidance },
        consent: { accepted: true, accepted_at: new Date().toISOString() },
      }, { withCredentials: true });
      toast.success("Assessment received! Your JAY 7™ Week 1 is being prepared.");
      navigate("/diet/client");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (member === undefined) return <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center"><Loader2 className="animate-spin text-[#89937C]" /></div>;
  if (member === null) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
        <Nav />
        <div className="max-w-md mx-auto px-6 pt-40 text-center" data-testid="onboarding-login-required">
          <div className="rounded-2xl border-2 border-[#C57B57] bg-[#C57B57]/10 p-5 text-left animate-pulse mb-8" data-testid="onboarding-check-email">
            <div className="font-semibold text-[#2D372B]">New here? Two quick steps:</div>
            <ol className="mt-2 text-sm text-[#5C665A] space-y-1 list-decimal pl-4">
              <li>Check your email for your JAY DIETS™ confirmation.</li>
              <li>Create an account (any email + your own password) or continue with Google — using the <u>same email</u>.</li>
            </ol>
          </div>
          <h1 className="font-serif-display text-3xl">Log in to begin your assessment</h1>
          <p className="mt-3 text-sm text-[#5C665A]">Use the same email you purchased or started your trial with.</p>
          <a href={`/login?redirect=${encodeURIComponent("/diet/onboarding")}&mode=signup`} data-testid="onboarding-login-link" className="mt-6 inline-flex rounded-full px-8 py-3.5 bg-[#2D372B] text-white font-medium hover:bg-[#89937C] transition-colors">Create Account / Log In</a>
        </div>
      </div>
    );
  }

  const fields = [
    <div key="s0" className="grid sm:grid-cols-2 gap-3">
      <Input data-testid="ob-name" placeholder="Full name *" value={f.name} onChange={set("name")} className={inputCls} />
      <Input data-testid="ob-age" type="number" placeholder="Age *" value={f.age} onChange={set("age")} className={inputCls} />
      <div>
        <label className="text-xs text-[#5C665A] block mb-1">Date of birth (for a birthday treat 🎂)</label>
        <Input data-testid="ob-dob" type="date" value={f.dob} onChange={set("dob")} className={inputCls} />
      </div>
      <select data-testid="ob-sex" value={f.sex} onChange={set("sex")} className={`${areaCls} h-11 py-0`}>
        <option value="">Sex</option><option>Female</option><option>Male</option><option>Prefer not to say</option>
      </select>
      <Input placeholder="Country" value={f.country} onChange={set("country")} className={inputCls} />
      <Input data-testid="ob-height" type="number" placeholder="Height (cm) *" value={f.height_cm} onChange={set("height_cm")} className={inputCls} />
      <Input data-testid="ob-weight" type="number" placeholder="Weight (kg) *" value={f.weight_kg} onChange={set("weight_kg")} className={inputCls} />
      <Input type="number" placeholder="Waist (cm, optional)" value={f.waist_cm} onChange={set("waist_cm")} className={inputCls} />
      <Input placeholder="Time zone" value={f.timezone} onChange={set("timezone")} className={inputCls} />
    </div>,
    <div key="s1" className="space-y-3">
      <p className="text-sm text-[#5C665A]">Select all that apply — you can pick more than one.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {GOALS.map((g) => {
          const on = f.goals.includes(g);
          return (
            <button key={g} type="button" data-testid={`goal-${g.toLowerCase().replace(/ /g, "-")}`} aria-pressed={on}
              onClick={() => setF({ ...f, goals: on ? f.goals.filter((x) => x !== g) : [...f.goals, g] })}
              className={`rounded-2xl border px-5 py-4 text-left transition-all flex items-center justify-between gap-2 ${on ? "border-[#2D372B] bg-[#2D372B] text-white" : "border-[#E5E2D9] bg-white hover:border-[#89937C]"}`}>
              <span className="font-medium">{g}</span>
              {on && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#C57B57] shrink-0"><Check size={12} className="text-white" /></span>}
            </button>
          );
        })}
      </div>
      <textarea rows={2} placeholder="Anything specific about your goal? (optional)" value={f.goal_note} onChange={set("goal_note")} className={areaCls} />
    </div>,
    <div key="s2" className="grid sm:grid-cols-2 gap-3">
      <Input placeholder="Occupation" value={f.occupation} onChange={set("occupation")} className={inputCls} />
      <Input placeholder="Sleep (e.g. 6–7 hrs, 11pm–6am)" value={f.sleep} onChange={set("sleep")} className={inputCls} />
      <Input placeholder="Stress level (low / medium / high)" value={f.stress} onChange={set("stress")} className={inputCls} />
      <Input placeholder="Work schedule (9–5, shifts…)" value={f.schedule} onChange={set("schedule")} className={inputCls} />
      <Input placeholder="How often do you travel?" value={f.travel} onChange={set("travel")} className={inputCls} />
      <Input placeholder="Usual meal times" value={f.meal_timing} onChange={set("meal_timing")} className={inputCls} />
    </div>,
    <div key="s3" className="space-y-3">
      <div>
        <p className="text-sm text-[#5C665A] mb-2">Diet preference — choose one:</p>
        <div className="flex flex-wrap gap-2">
          {DIET_TYPES.map((d) => {
            const on = f.diet_type === d;
            return (
              <button key={d} type="button" data-testid={`diet-type-${d.toLowerCase().replace(/ /g, "-")}`} aria-pressed={on}
                onClick={() => setF({ ...f, diet_type: on ? "" : d })}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-all inline-flex items-center gap-1.5 ${on ? "border-[#2D372B] bg-[#2D372B] text-white" : "border-[#E5E2D9] bg-white hover:border-[#89937C]"}`}>
                {on && <Check size={12} className="text-[#E8C9B8]" />} {d}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <p className="text-sm text-[#5C665A] mb-2">JAY 7™ Global Series — how would you like your week's cuisine? (optional)</p>
        <div className="flex flex-wrap gap-2">
          {[["single", "One cuisine all week"], ["mix", "Mix — different cuisine per day"]].map(([mode, label]) => {
            const on = f.cuisine_mode === mode;
            return (
              <button key={mode} type="button" data-testid={`cuisine-mode-${mode}`} aria-pressed={on}
                onClick={() => setF({ ...f, cuisine_mode: on ? "" : mode })}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-all inline-flex items-center gap-1.5 ${on ? "border-[#C57B57] bg-[#C57B57] text-white" : "border-[#E5E2D9] bg-white hover:border-[#89937C]"}`}>
                {on && <Check size={12} />} {label}
              </button>
            );
          })}
        </div>
        {f.cuisine_mode === "single" && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="cuisine-single-picker">
            {GS_CUISINES.map(([id, label]) => {
              const on = f.cuisine_single === id;
              return (
                <button key={id} type="button" data-testid={`cuisine-pick-${id}`} aria-pressed={on}
                  onClick={() => setF({ ...f, cuisine_single: on ? "" : id, cuisine_single_region: "" })}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all ${on ? "border-[#2D372B] bg-[#2D372B] text-white" : "border-[#E5E2D9] bg-white hover:border-[#89937C]"}`}>
                  {label}
                </button>
              );
            })}
          </div>
        )}
        {f.cuisine_mode === "single" && f.cuisine_single === "indian" && (
          <div className="mt-3" data-testid="indian-region-picker-single">
            <p className="text-xs text-[#5C665A] mb-1.5">Which Indian style? (optional)</p>
            <div className="flex flex-wrap gap-2">
              {[["", "Pan-Indian"], ...INDIAN_REGIONS].map(([id, label]) => {
                const on = f.cuisine_single_region === id;
                return (
                  <button key={id || "pan"} type="button" data-testid={`indian-region-pick-${id || "pan"}`} aria-pressed={on}
                    onClick={() => setF({ ...f, cuisine_single_region: id })}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all ${on ? "border-[#C57B57] bg-[#C57B57] text-white" : "border-[#E5E2D9] bg-white hover:border-[#89937C]"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {f.cuisine_mode === "mix" && (
          <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="cuisine-mix-picker">
            {f.cuisine_days.map((v, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs font-semibold text-[#5C665A] w-12 shrink-0">Day {i + 1}</span>
                <select data-testid={`cuisine-day-${i + 1}`} value={v}
                  onChange={(e) => {
                    const days = [...f.cuisine_days]; days[i] = e.target.value;
                    const regs = [...f.cuisine_day_regions]; if (e.target.value !== "indian") regs[i] = "";
                    setF({ ...f, cuisine_days: days, cuisine_day_regions: regs });
                  }}
                  className="flex-1 bg-white border border-[#E5E2D9] rounded-xl h-10 px-3 text-sm">
                  <option value="">Choose cuisine…</option>
                  {GS_CUISINES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
                {v === "indian" && (
                  <select data-testid={`indian-region-day-${i + 1}`} value={f.cuisine_day_regions[i]}
                    onChange={(e) => { const regs = [...f.cuisine_day_regions]; regs[i] = e.target.value; setF({ ...f, cuisine_day_regions: regs }); }}
                    className="w-36 shrink-0 bg-white border border-[#C57B57]/40 rounded-xl h-10 px-2 text-xs text-[#AD6A48]">
                    <option value="">Pan-Indian</option>
                    {INDIAN_REGIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input placeholder="Cuisine / cultural preferences" value={f.cuisine} onChange={set("cuisine")} className={inputCls} />
        <Input placeholder="Food dislikes" value={f.dislikes} onChange={set("dislikes")} className={inputCls} />
        <Input placeholder="Allergies / intolerances" value={f.allergies} onChange={set("allergies")} className={inputCls} />
        <Input placeholder="Cooking access (self / cook / tiffin…)" value={f.cooking} onChange={set("cooking")} className={inputCls} />
        <Input placeholder="Typical snacks" value={f.snacks} onChange={set("snacks")} className={inputCls} />
        <Input placeholder="Beverages (tea, coffee, alcohol…)" value={f.beverages} onChange={set("beverages")} className={inputCls} />
      </div>
      <textarea rows={2} placeholder="Typical meals in your week" value={f.typical_meals} onChange={set("typical_meals")} className={areaCls} />
    </div>,
    <div key="s4" className="grid sm:grid-cols-2 gap-3">
      <Input data-testid="ob-activities" placeholder="Activities (gym, walking, yoga, sports…)" value={f.activities} onChange={set("activities")} className={inputCls} />
      <Input placeholder="Training frequency (e.g. 4×/week)" value={f.frequency} onChange={set("frequency")} className={inputCls} />
    </div>,
    <textarea key="s5" data-testid="ob-typical-day" rows={6} placeholder={"Walk us through a typical day of eating — from waking up to bedtime.\ne.g. 7am tea + biscuits, 9am poha, 1pm dal-rice-sabzi…"} value={f.typical_day} onChange={set("typical_day")} className={areaCls} />,
    <div key="s6" className="space-y-3">
      <textarea rows={2} placeholder="Relevant health conditions (if any)" value={f.conditions} onChange={set("conditions")} className={areaCls} />
      <div className="grid sm:grid-cols-2 gap-3">
        <Input placeholder="Medications" value={f.medications} onChange={set("medications")} className={inputCls} />
        <Input placeholder="Supplements" value={f.supplements} onChange={set("supplements")} className={inputCls} />
      </div>
      <textarea rows={2} placeholder="Recent lab information you'd like to share (optional)" value={f.labs} onChange={set("labs")} className={areaCls} />
      <Input placeholder="Existing professional nutrition guidance (if any)" value={f.existing_guidance} onChange={set("existing_guidance")} className={inputCls} />
      <p className="text-xs text-[#5C665A]">This information helps scope your coaching appropriately. JAY DIETS does not diagnose or treat medical conditions.</p>
    </div>,
    <div key="s7" className="space-y-4">
      <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5 text-sm text-[#5C665A] space-y-2 leading-relaxed">
        <p>By continuing you acknowledge that:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>JAY DIETS provides general nutrition coaching — not medical diagnosis or treatment.</li>
          <li>You will seek professional medical advice where appropriate.</li>
          <li>The information you've provided is accurate to the best of your knowledge.</li>
          <li>Communication may happen over WhatsApp, email, Zoom, Webex or calls.</li>
          <li>Third-party tracking tools are entirely optional.</li>
        </ul>
      </div>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="ob-consent-label">
        <input type="checkbox" data-testid="ob-consent" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-1 h-4 w-4 accent-[#2D372B]" />
        <span className="text-sm">I have read and accept the coaching scope acknowledgement above.</span>
      </label>
      <div className="rounded-2xl border-2 border-[#89937C] bg-[#89937C]/10 p-4" data-testid="ob-final-note">
        <p className="text-sm font-semibold text-[#2D372B]">This is the final step.</p>
        <p className="text-sm text-[#5C665A] mt-1">Pressing <span className="font-semibold text-[#2D372B]">SUBMIT</span> sends everything you've filled in securely to Coach Jay, who will personally prepare your JAY 7™ plan.</p>
      </div>
    </div>,
  ];

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <Nav />
      <div className="max-w-2xl mx-auto px-6 pt-32 md:pt-40 pb-20">
        <div className="overline mb-2">JAY DIETS™ Assessment</div>
        <h1 className="font-serif-display text-3xl md:text-4xl tracking-tight">Step {step + 1} — {STEPS[step]}</h1>
        <div className="mt-4 h-1.5 rounded-full bg-[#E5E2D9] overflow-hidden">
          <div className="h-full bg-[#C57B57] transition-all duration-500" style={{ width: `${((step + 1) / 8) * 100}%` }} data-testid="ob-progress" />
        </div>
        <div className="mt-2 text-right text-[11px] text-[#9AA294] h-4" data-testid="ob-draft-status">
          {draftState === "saving" && "Saving draft…"}
          {draftState === "saved" && "✓ Draft saved — you can safely leave and continue later"}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }} className="mt-8">
            {fields[step]}
          </motion.div>
        </AnimatePresence>
        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <button type="button" data-testid="ob-back" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 border border-[#E5E2D9] text-sm font-medium disabled:opacity-40 hover:bg-white transition-colors">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="flex items-center gap-3">
            {step === 7 && (
              <button type="button" data-testid="ob-logout" onClick={logout}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 border border-[#A05252]/50 text-[#A05252] text-sm font-medium hover:bg-[#A05252]/10 transition-colors">
                <LogOut size={14} /> Logout
              </button>
            )}
            <button type="button" data-testid="ob-next" onClick={next} disabled={submitting}
              className={`inline-flex items-center gap-2 rounded-full px-8 py-3 text-white text-sm font-semibold transition-colors disabled:opacity-60 ${step === 7 ? "bg-[#C57B57] hover:bg-[#AD6A48]" : "bg-[#2D372B] hover:bg-[#89937C]"}`}>
              {submitting ? <Loader2 size={14} className="animate-spin" /> : step === 7 ? <Send size={14} /> : <ArrowRight size={14} />}
              {step === 7 ? "SUBMIT — Send to Coach" : "Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
